import React, { useState, useEffect, useRef } from 'react';
import { MessageSquare } from '../icons';
import Tooltip from './Tooltip';

export interface ChatConversation {
  other_user_id: number;
  other_user_name: string;
  other_user_initials: string;
  last_message: string;
  last_message_time: string;
  unread_count: number;
}

interface ChatBellProps {
  conversations: ChatConversation[];
  unreadCount: number;
  onConversationClick: (userId: number) => void;
  onOpenChat: () => void;
}

/**
 * ChatBell component - part of unified TopBar
 * Shows chat icon with unread count badge and dropdown with recent conversations
 */
export const ChatBell: React.FC<ChatBellProps> = ({
  conversations,
  unreadCount,
  onConversationClick,
  onOpenChat
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const formatTime = (dateStr: string) => {
    try {
      const date = new Date(dateStr);
      const now = new Date();
      const diffMs = now.getTime() - date.getTime();
      const diffMins = Math.floor(diffMs / 60000);
      
      if (diffMins < 1) return 'teraz';
      if (diffMins < 60) return `${diffMins} min temu`;
      
      const diffHours = Math.floor(diffMins / 60);
      if (diffHours < 24) return `${diffHours}h temu`;
      
      const diffDays = Math.floor(diffHours / 24);
      if (diffDays < 7) return `${diffDays}d temu`;
      
      return date.toLocaleDateString('pl-PL', { day: '2-digit', month: '2-digit' });
    } catch {
      return '';
    }
  };

  const truncateMessage = (message: string, maxLength: number = 50) => {
    if (message.length <= maxLength) return message;
    return message.substring(0, maxLength) + '...';
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <Tooltip content="Czat" position="bottom">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="relative flex items-center justify-center text-text hover:text-accent transition-colors p-1 cursor-pointer"
          aria-label="Czat"
        >
          <MessageSquare className="w-5 h-5" />
          
          {unreadCount > 0 && (
            <span 
              style={{ 
                position: 'absolute',
                top: -2,
                right: -2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: 16,
                height: 16,
                padding: unreadCount > 9 ? '0 4px' : 0,
                fontSize: '10px',
                fontWeight: 700,
                lineHeight: 1,
                color: '#ffffff',
                backgroundColor: '#3b82f6',
                borderRadius: '9999px',
                boxShadow: '0 1px 2px rgba(0,0,0,0.2)'
              }}
            >
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </button>
      </Tooltip>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-96 bg-white border border-border rounded-lg shadow-lg z-50 max-h-[600px] flex flex-col" style={{ backgroundColor: '#ffffff' }}>
          <div className="px-4 py-3 border-b border-border flex items-center justify-between">
            <h3 className="text-sm font-semibold text-text">
              Czat {unreadCount > 0 && `(${unreadCount})`}
            </h3>
            <button
              onClick={() => {
                setIsOpen(false);
                onOpenChat();
              }}
              className="text-xs text-blue-600 hover:text-blue-700 font-medium"
            >
              Nowa wiadomość
            </button>
          </div>

          <div className="flex-1 overflow-y-auto">
            {conversations.length === 0 ? (
              <div className="px-4 py-8 text-center text-sm" style={{ color: '#64748b' }}>
                <MessageSquare className="w-12 h-12 mx-auto mb-2 opacity-30" style={{ color: '#94a3b8' }} />
                <p>Brak konwersacji</p>
              </div>
            ) : (
              <div className="divide-y divide-slate-200">
                {conversations.map((conv) => {
                  const safeName = conv.other_user_name?.trim() || `Użytkownik #${conv.other_user_id}`;
                  const previewMessage = conv.last_message?.trim()
                    ? truncateMessage(conv.last_message.trim())
                    : 'Brak wiadomości w tej rozmowie';
                  const timeLabel = conv.last_message_time ? formatTime(conv.last_message_time) : '';
                  const initials = conv.other_user_initials?.trim() || safeName.slice(0, 2).toUpperCase();

                  return (
                    <div
                      key={conv.other_user_id}
                      onClick={() => {
                        setIsOpen(false);
                        onConversationClick(conv.other_user_id);
                      }}
                      className="px-4 py-3 cursor-pointer hover:bg-slate-50 transition-colors"
                    >
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0 flex items-center justify-center w-10 h-10 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 text-white font-semibold text-sm">
                          {initials}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2 mb-1">
                            <p className="text-sm font-semibold" style={{ color: '#0f172a' }}>
                              {safeName}
                            </p>
                            {conv.unread_count > 0 && (
                              <span className="flex-shrink-0 flex items-center justify-center min-w-[20px] h-[20px] px-1.5 text-[10px] font-bold text-white bg-blue-500 rounded-full">
                                {conv.unread_count > 99 ? '99+' : conv.unread_count}
                              </span>
                            )}
                          </div>
                          <p className="text-xs line-clamp-1 mb-1" style={{ color: '#475569' }}>
                            {previewMessage}
                          </p>
                          <p className="text-xs" style={{ color: '#64748b' }}>
                            {timeLabel || '—'}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

